import { CHART_DIMENSIONS } from '@/components/Charts/constants';
import { TokenPriceChart } from '@/components/TokenPriceChart';
import type { LimitPriceTokenSide } from '@/components/TransactionModule/LimitMode/LimitPriceShortcuts';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { ExpandIcon } from '@/ui/Icons/ExpandIcon';
import { ShrinkIcon } from '@/ui/Icons/ShrinkIcon';

import { TokenResult } from '@/utils/0x/token-registry.types';

import type { LimitChartLine } from './TradeLimitChart';

type TradeLimitNativeChartProps = {
  buyToken: TokenResult | undefined;
  sellToken: TokenResult | undefined;
  side: LimitPriceTokenSide;
  line: LimitChartLine;
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
};

/**
 * Native SVG chart for advanced orders. Mirrors the TradingView pair views:
 * `side === 'buy'` charts the buy token in sell-token units and vice versa.
 */
export const TradeLimitNativeChart = ({
  buyToken,
  sellToken,
  side,
  line,
  isExpanded,
  setIsExpanded,
}: TradeLimitNativeChartProps) => {
  const token = side === 'buy' ? buyToken : sellToken;
  const quoteToken = side === 'buy' ? sellToken : buyToken;

  return (
    <div className="flex flex-col gap-3 pt-5 pb-3">
      <div className="flex items-center justify-between gap-2 px-3 tablet:px-6">
        <span className={fontStyles({ variant: 'LABEL_LG' })}>
          {token && quoteToken ? `${token.symbol} / ${quoteToken.symbol}` : undefined}
        </span>
        <button
          type="button"
          aria-label={isExpanded ? 'Collapse chart' : 'Expand chart'}
          className={buttons({
            size: 'small',
            shape: 'pill',
            color: 'gray',
            class: ['w-9', 'h-9', 'p-0'],
          })}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <ShrinkIcon className="size-4" /> : <ExpandIcon className="size-4" />}
        </button>
      </div>
      <TokenPriceChart
        token={token}
        quoteToken={quoteToken}
        chartType="candlestick"
        height={CHART_DIMENSIONS.BASE_HEIGHT}
        showLabels
        showTimeframes
        showTokenPrice
        autoRefresh
        priceLines={
          line.enabled && line.price != null
            ? [
                {
                  key: 'order',
                  value: line.price,
                  label: line.label,
                  color: `var(--color-${line.color})`,
                  onValueChange: line.onPriceChange,
                },
              ]
            : undefined
        }
      />
    </div>
  );
};
